import React, { forwardRef } from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  /** Label for the select */
  label?: string;
  /** Error message to display */
  error?: string;
  /** Helper text below the select */
  helperText?: string;
  /** Options to display */
  options: SelectOption[];
  /** Placeholder text */
  placeholder?: string;
  /** Full width select */
  fullWidth?: boolean;
}

/**
 * Select component with label, error, and helper text support
 * 
 * @example
 * ```tsx
 * <Select
 *   label="Category"
 *   options={[
 *     { value: 'tech', label: 'Technology' },
 *     { value: 'marketing', label: 'Marketing' },
 *   ]}
 *   placeholder="Select a category"
 * />
 * ```
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      options,
      placeholder,
      fullWidth = true,
      id,
      ...props
    },
    ref
  ) => {
    const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className={clsx(fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            className={twMerge(
              clsx(
                'block w-full rounded-md border shadow-sm',
                'text-sm appearance-none',
                'transition-colors duration-200',
                'focus:outline-none focus:ring-2 focus:ring-offset-0',
                // Normal state
                !error && 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500',
                // Error state
                error && 'border-red-300 focus:border-red-500 focus:ring-red-500 text-red-900',
                // Disabled state
                'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
                // Padding
                'pl-3 pr-10 py-2',
                className
              )
            )}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </div>
        </div>
        {error && (
          <p id={`${selectId}-error`} className="mt-1 text-sm text-red-600">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p id={`${selectId}-helper`} className="mt-1 text-sm text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
